import { getFrameworkServiceUrl, getFrameworkVar, getFrameworkVars, isAppCanBeLaunched } from '../../framework/states'
import {
  MSP_PATH_PREFIX,
  SDWAN_APP_ID,
  SDWAN_PATH_PREFIX,
  SDWAN_MICRO_APP_NAME,
  MSP_MICRO_APP_NAME,
  HUB_URL_KEY,
  PA_DEFAULT_ROUTE,
  FAWKES_APP_ID,
  MGMT_OVERVIEW_ROUTE,
  ASC_PATH_PREFIX,
  ASC_PORTAL_MICRO_APP,
  ASC_TIER_1_SUPPORT_ROLE,
  FAWKES_MICRO_APP_NAME,
  CN_REGION,
  DLP_MICRO_APP_NAME,
  SAAS_MICRO_APP_NAME,
  PANORAMA,
} from '../../framework/constants'
import { getAuthState, getMainState } from '../../framework/store'
import { hasMicroApp } from '../../framework/microApps'
import { hasTsgSupport } from '../../framework/tsg'

declare global {
  interface Window {
    __admin_cluster_url?: string
    __explorer_api_server?: string
    __insights_api_server?: string
    __reporting_api_server?: string
    __insights_map_server?: string
    __dem_api_server?: string
  }
}

export const FAWKES_CONFIG = 'adelphi'
export const PANORAMA_CONFIG = PANORAMA

export const isUserRoleHasManagePermission = (): boolean | undefined => {
  const loggedInUser = getMainState()?.loggedInUser
  if (!loggedInUser) {
    return // not loaded yet
  }
  return Array.isArray(loggedInUser.roles) && loggedInUser.roles.some(r => r !== 'auditadmin')
}
export const isUserRoleSaaSAdmin = (): boolean => {
  const roles = getMainState()?.loggedInUser?.roles
  return Array.isArray(roles) && roles.some(r => r === 'saasadmin')
}
export const isUserRoleDlpOrDataSecurityAdmin = (): boolean => {
  const roles = getMainState()?.loggedInUser?.roles
  return Array.isArray(roles) && roles.some(r => r === 'datalosspreventionadmin' || r === 'datasecurityadmin')
}
export const isUserRoleAdemSupport = (): boolean => {
  const roles = getMainState()?.loggedInUser?.roles
  return Array.isArray(roles) && roles.some(r => r === 'ademsupport')
}


export default class StartupManager {
  static FAWKES_CONFIG = FAWKES_CONFIG
  static PANORAMA_CONFIG = PANORAMA_CONFIG

  static isDemActive(): boolean {
    return Boolean(getMainState()?.tenant?.demActivated)
  }

  static getConfigManagedType(): string {
    const main = getMainState()
    const type = main?.tenant?.type || FAWKES_CONFIG
    return type
  }

  static isMigrationStatusFetched(): boolean {
    const main = getMainState()
    const isFetched = Boolean(main?.migrationStatusFetched)
    return isFetched
  }

  static isMigrationStateEligibleForFawkesUI(): boolean {
    const main = getMainState()
    const status = main?.migrationStatus
    const hasStatus = [
      'LOAD_CFG_DONE',
      'LICENSE_TRANSFER_DONE',
      'LICENSE_TRANSFER_FAIL',
      'MIGRATION_FAILED',
      'COMMIT_CFG_FAILED'
    ].includes(status)

    return hasStatus
  }

  static hideManagementUIBasedOnMigrationState(): boolean {
    const status = getMainState()?.migrationStatus

    return status === 'MIGRATION_DONE'
  }

  static isMultiTenancy(): boolean {
    return Boolean(getMainState()?.subTenantInfo?.isMultiTenancy)
  }

  static getManagedAppName(): string {
    const type = StartupManager.getConfigManagedType()
    if (type === PANORAMA_CONFIG) {
      return 'prisma_access_panorama'
    }
    return 'prisma_access'
  }

  static getFawkesDefaultRedirect(): string {
    if (hasTsgSupport() && hasMicroApp(ASC_PORTAL_MICRO_APP) && getAuthState()?.access?.hasRoleOnly(ASC_TIER_1_SUPPORT_ROLE)) {
      return ASC_PATH_PREFIX // only have tier 1 role (ignore browser role), redirect to ASC
    }
    if (getFrameworkVar('redirect_sase_to_scm') === 'exclude-sdwan' && getAuthState().instances?.isRunningStatus(SDWAN_APP_ID)) {
      return SDWAN_PATH_PREFIX
    }
    const type = StartupManager.getConfigManagedType()
    let redirect = MGMT_OVERVIEW_ROUTE
    if (type === PANORAMA_CONFIG && isUserRoleAdemSupport()) {
      redirect = '/dem/dem-summary'
    }
    else if ((!hasMicroApp(FAWKES_MICRO_APP_NAME) && getAuthState().instances?.getPlatformRegion() === CN_REGION) ||
      (type === PANORAMA_CONFIG && !StartupManager.isMigrationStateEligibleForFawkesUI()) || isUserRoleHasManagePermission() === false) {
      redirect = '/insights/sase-summary'
    }
    else if (isUserRoleSaaSAdmin() && hasMicroApp(SAAS_MICRO_APP_NAME)) {
      redirect = '/manage/saas-security'
    }
    else if (isUserRoleDlpOrDataSecurityAdmin() && hasMicroApp(DLP_MICRO_APP_NAME)) {
      redirect = '/manage/prisma-access/data-loss-prevention'
    }
    return redirect
  }

  static defaultRedirect({
    vars = getFrameworkVars(),
    auth = getAuthState(),
    singleInstance = false,
  } = {}): string {
    if (vars.sase && auth?.tsg_id && auth.tsgs) {
      if (hasMicroApp(ASC_PORTAL_MICRO_APP) && auth.access?.hasRoleOnly(ASC_TIER_1_SUPPORT_ROLE)) {
        return ASC_PATH_PREFIX
      }
      if (vars.redirect_sase_to_scm === 'exclude-sdwan' &&
        auth.instances?.isRunningStatus(SDWAN_APP_ID) && hasMicroApp(SDWAN_MICRO_APP_NAME)) {
        return SDWAN_PATH_PREFIX
      }
      if (!singleInstance && auth.tsgs.hasMsp && auth.tsgs.hasSubTenants(auth.tsg_id) && hasMicroApp(MSP_MICRO_APP_NAME)) {
        return vars.msp_route || vars.default_route || MSP_PATH_PREFIX
      }
      if (auth.instances?.isRunningStatus(SDWAN_APP_ID) && !auth.instances.isFawkesRunning && hasMicroApp(SDWAN_MICRO_APP_NAME)) {
        return SDWAN_PATH_PREFIX
      }
      if (auth.instances?.isFawkesRunning) {
        const fawkesDefaultRoute = vars.apps_can_launch?.[FAWKES_APP_ID]?.default_route || PA_DEFAULT_ROUTE
        return fawkesDefaultRoute
      }
      if (vars.apps_can_launch && auth.instances) {
        for (const appId of Object.keys(vars.apps_can_launch)) {
          if (![SDWAN_APP_ID, FAWKES_APP_ID].includes(appId) &&
            auth.instances.isRunningStatus(appId) && isAppCanBeLaunched(auth.instances.get(appId))) {
            const appDefaultRoute = vars.apps_can_launch[appId].default_route
            if (appDefaultRoute) {
              return appDefaultRoute
            }
          }
        }
      }
      if (vars.default_route) { // && !vars.default_route.startsWith(MSP_PATH_PREFIX)
        return vars.default_route
      }
      return '/'
    }
    if (vars.default_route && (!singleInstance || !vars.default_route.startsWith(MSP_PATH_PREFIX))) {
      return vars.default_route
    }
    return StartupManager.getFawkesDefaultRedirect()
  }

  static getAppPortalUrl(authInfo?: any): string {
    const url = authInfo?.extraInfo?.appPortal || getFrameworkServiceUrl(HUB_URL_KEY)
    return url
  }

  static getPaiFQDNs(): { apiServer?: string, mapService?: string } {
    const main = getMainState()
    const paifqdn = main?.fqdnInfo?.msg?.paifqdn
    const apiServer = paifqdn || window.__insights_api_server
    const mapService = paifqdn || window.__insights_map_server
    return {
      apiServer,
      mapService,
    }
  }

  static getReportingFQDNs(): { apiServer?: string, paiFQDN?: string } {
    const main = getMainState()
    const paiFQDN = main?.fqdnInfo?.msg?.paifqdn || window.__insights_api_server
    const apiServer = window.__reporting_api_server
    return {
      apiServer,
      paiFQDN
    }
  }

  static getNyxServerFQDN(): string | undefined {
    return window.__explorer_api_server
  }

  static getDemServerFqdn(): string | undefined {
    const main = getMainState()
    const apiServer = main?.fqdnInfo?.msg?.demfqdn || window.__dem_api_server
    return apiServer
  }
}
